import React, { useCallback, useEffect, useState } from 'react';

import { blue } from '@mui/material/colors';

import { makeStyles } from '@material-ui/core/styles';

import '../Main.css';

import { create }         from '../util';
import Attender           from '../attender/Attender';
import NewSchedule        from './NewSchedule';
import QuickAttending     from './ScheduledAttender';
import Schedule           from './Schedule';
import SchedulesPrintable from './SchedulesPrintable';

const recreate = {
  timetable: (allAttendings = undefined) => {
    if (allAttendings === undefined) {
      return [[], [], [], [], [], [], []];
    } else {
      let dailyAttendings = recreate.timetable();
      for (const schedule of allAttendings) {
        dailyAttendings[schedule['schedule.day']].push(schedule);
      }

      let newTimetable = recreate.timetable();
      for (let day = 0; day < 7; day++) {
        dailyAttendings[day].sort((a1, a2) =>
          a1['schedule.seconds_from'] - a2['schedule.seconds_from'] ||
          a1['schedule.seconds_to'] - a2['schedule.seconds_to'] ||
          (a1['attender.name'] || '').localeCompare(a2['attender.name'] || '')
        );

        let scheduleRowid = -1;
        for (const attending of dailyAttendings[day]) {
          if (scheduleRowid !== attending['schedule.rowid']) {
            newTimetable[attending['schedule.day']].push(create.schedule(
              attending['schedule.rowid'],
              [],
              attending['schedule.day'],
              attending['schedule.name'],
              attending['schedule.note'],
              attending['schedule.seconds_from'],
              attending['schedule.seconds_to'],
            ));
            scheduleRowid = attending['schedule.rowid'];
          }

          if (attending['attender.rowid']) {
            newTimetable[attending['schedule.day']][
              newTimetable[attending['schedule.day']].length - 1
            ].attendings.push(create.attending(
              attending['attender.rowid'],
              attending['attender.name'],
              attending['attender.picture64'],
              attending['MAX(issued.used_at)'],
            ));
          }
        }
      }
      return newTimetable;
    }
  },
};

const style = {
  schedules: makeStyles((theme) => ({
    column: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      gap: theme.spacing(2),
    },
    root: {
      display: 'flex',
      flexDirection: 'row',
      gap: theme.spacing(2),
      padding: theme.spacing(8),
    },
    scheduleDay: {
      borderColor: blue[900],
      borderStyle: 'solid',
      borderWidth: '1px',
      color: blue[800],
      fontWeight: 'bold',
      padding: theme.spacing(2),
      textAlign: 'center',
    },
  })),
}

export default function Schedules(props) {
  const classes = style.schedules();

  const [ quickAttending, setQuickAttending ] = useState(create.quickAttending());
  const [ revision      , setRevision       ] = useState(0);
  const [ timetable     , setTimetable      ] = useState(recreate.timetable());

  const {
    attender,
    newSchedule,
    path,
    request,
    setNewSchedule,
    ticketer,
  } = props.context.getStates();

  const deleteSchedule = useCallback((schedule) => {
    request.postSecurely('/schedule/delete', {
      schedule,
    }, (response) => {
      if ('error' in response) {
        console.error(response);
      } else {
        setRevision(revision + 1);
        setNewSchedule(create.schedule());
      }
    });
  }, [
    request,
    revision,
    setNewSchedule,
  ]);

  const fetchSchedules = useCallback(() => {
    request.postSecurely('/schedules/attendings', {
      ticketer
    }, (response) => {
      if ('error' in response) {
        console.error(response);
      } else if ('attendings' in response) {
        setTimetable(recreate.timetable(response.attendings));
      }
    });
  }, [request, ticketer]);

  const updateSchedule = useCallback(() => {
    request.postSecurely('/schedule/update', {
      ticketer,
      ...newSchedule,
    }, (response) => {
      if ('error' in response) {
        console.error(response);
      } else {
        setNewSchedule(create.schedule());
        setRevision((oldRevision) => (oldRevision + 1));
      }
    });
  }, [
    newSchedule,
    request,
    setNewSchedule,
    ticketer,
  ]);

  useEffect(() => {
    if (path === '/schedules') {
      fetchSchedules();
    }
  }, [
    attender,
    fetchSchedules,
    path,
    revision,
  ]);

  if (path.startsWith('/schedules/attender')) {
    return (
      <Attender context={props.context} />
    );
  } else if (path.startsWith('/schedules/print')) {
    return (
      <div className='printable'>
        <SchedulesPrintable timetable={timetable}
          context={props.context}
        />
      </div>
    );
  } else if (path.startsWith('/schedules')) {
    return (<>
      <NewSchedule context={props.context}
        deleteSchedule={deleteSchedule}
        setRevision={setRevision}
        updateSchedule={updateSchedule}
      />

      <QuickAttending quickAttending={quickAttending}
        context={props.context}
        setQuickAttending={setQuickAttending}
        revision={revision}
        setRevision={setRevision}
      />

      <div className={classes.root}>
        {[
          {value: 1, label: '월'},
          {value: 2, label: '화'},
          {value: 3, label: '수'},
          {value: 4, label: '목'},
          {value: 5, label: '금'},
          {value: 6, label: '토'},
          {value: 0, label: '일'},
        ].map(day => (
          <div key={day.value} className={classes.column}>
            <div className={classes.scheduleDay}>
              {`${day.label}요일`}
            </div>
            {timetable[day.value].map((schedule, index) => (
              <Schedule key={index} schedule={schedule}
                context={props.context}
                quickAttending={quickAttending}
                setQuickAttending={setQuickAttending}
              />
            ))}
          </div>
        ))}
      </div>
    </>);
  } else {
    return null;
  }
}